import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  CardMedia,
  Dialog,
  Tooltip,
  Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  checkForCompletedApplication,
  checkIfApplicationIsNonIndividual,
  getApplicationStatus,
  getFirstHolderName,
  handleDownload,
} from '../../utils/utilityFunctions';
import { ConfirmationDialog, LoadingDialog } from '../commonComponents';
import React, { useEffect, useMemo, useState } from 'react';
import { getAllApplications, getApplicationDetails } from '../../redux-store/actions/application';
import { useDispatch, useSelector } from 'react-redux';
import {
  ApplicationProps,
  investmentDetailsAplicationRef,
} from '../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../DataTable';
import {
  APPLICATION_LISTING_STATUS,
  NCT_STATUS,
  POA_APPLICATION_STATUS,
  REJECTED_ROLES,
  USER_ROLES,
} from '../../utils/constant';
import { formatShortDateAndTime } from '../../utils/date';
import CloseIcon from '@mui/icons-material/Close';
import { ApplicationRefs } from '../../redux-store/actions/onBoarding';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RootStateType } from '../../redux-store/reducers';
import { SxProps } from '@mui/system';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { changeStatusToDraft } from '../../redux-store/actions/adminSupport';
import { fetchMismatchInvestors, getAllNctApplications } from '../../redux-store/actions/nct';
import {
  ApplicationNctProps,
  GetAllNctResponseBody,
  NctRequestData,
  NctRequestDataArray,
} from '../../redux-store/types/nct';
import AutorenewIcon from '@mui/icons-material/Autorenew';

export const getInvestorTableHeader = (isMobile = false): ColumnType[] => {
  const extraStartColumn: ColumnType = {
    header: '',
    isExpandableIcon: true,
  };

  const mainColumns: ColumnType[] = [
    {
      header: 'Name',
      label: 'name',
      valueGetter: ({ name }: NctRequestData) => name || 'N/A',
    },
    {
      header: 'PAN Number',
      valueGetter: ({ panNumber }: NctRequestData) => panNumber || 'N/A',
    },
    {
      header: 'Mobile Number',
      label: 'mobile',
      valueGetter: ({ phone }: NctRequestData) => phone || 'N/A',
    },
    {
      header: 'Email ID',
      label: 'email',
      valueGetter: ({ email }: NctRequestData) => email || 'N/A',
    },
    {
      header: 'CVL KRA Status',
      label: 'all_cvl_kra_status',
      valueGetter: ({ all_cvl_kra_status = '' }: NctRequestData) =>
        NCT_STATUS[all_cvl_kra_status] || 'N/A',
    },
    {
      header: 'Change Request Status',
      label: 'all_change_request_status',
      valueGetter: ({ all_change_request_status = '' }: NctRequestData) =>
        NCT_STATUS[all_change_request_status] || 'N/A',
    },
  ];

  const extraEndColumn: ColumnType = {
    header: '',
    isExpandableIcon: true,
  };

  if (isMobile) {
    // Skip the start column, include the end column
    return [...mainColumns, extraEndColumn];
  } else {
    // Include the start column, skip the end column
    return [extraStartColumn, ...mainColumns];
  }
};

export const getApplicationTableHeader = (role: string): ColumnType[] => {
  // return [
  //   {
  //     header: 'Mode of Holding',
  //     renderCell: (row: ApplicationNctProps) => {
  //       return (
  //         <Typography
  //           sx={{
  //             textTransform: 'capitalize',
  //           }}>
  //           {(row.modeOfHolding === 'either or survivor' ? 'Joint' : row.modeOfHolding) || 'Single'}
  //         </Typography>
  //       );
  //     },
  //   },
  //   {
  //     header: 'Distributor Name',
  //     label: 'viewDistributorRM',
  //     valueGetter: (row: ApplicationNctProps) =>
  //       row.distributor?.name || row.distributor?.arnCode || 'N/A',
  //   },
  //   {
  //     header: 'RM Name',
  //     valueGetter: (row: ApplicationNctProps) => row.rm?.name || 'N/A',
  //   },
  // ];
  return [
    {
      header: 'Name',
      label: 'name',
      valueGetter: ({ applicants = [] }: ApplicationNctProps) =>
        getFirstHolderName(applicants) || 'N/A',
    },
    {
      header: 'Application Number',
      label: 'applicationNumber',
      valueGetter: (row: ApplicationNctProps) => row.applicationNumber || 'N/A',
    },
    {
      header: 'Scheme Name',
      label: 'schemeName',
      valueGetter: ({ scheme }: ApplicationNctProps) => scheme?.schemeName || 'N/A',
    },
    {
      header: 'CVL KRA Status',
      label: 'cvl_kra_status',
      valueGetter: ({ cvl_kra_status = '' }: ApplicationNctProps) =>
        NCT_STATUS[cvl_kra_status] || 'N/A',
    },
    {
      header: 'Change Request Status',
      label: 'change_request_status',
      valueGetter: ({ change_request_status = '' }: ApplicationNctProps) =>
        NCT_STATUS[change_request_status] || 'N/A',
    },
    {
      header: 'Application Status',
      renderCell: (row: ApplicationNctProps) => {
        const applicationStatus = getApplicationStatus(row.status, role);
        return (
          <Typography
            className={applicationStatus}
            sx={{
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: 14,
              color: '#B78813',
              '&.Draft': {
                color: 'rgba(51, 127, 201, 0.7)',
              },
              '&.Completed': {
                fontWeight: 600,
                color: 'rgba(35, 133, 63, 0.7)',
              },
              '&.Rejected': {
                color: '#BD2932 !important',
                fontWeight: 500,
              },
            }}
            component={RouterLink}
            to={
              checkIfApplicationIsNonIndividual(row)
                ? `/non-individual-kyc-details/${row.id}`
                : `/individual-kyc-details/${row.id}`
            }>
            {applicationStatus}
          </Typography>
        );
      },
    },
  ];
};

export default function InvestorManagementList({
  isLoading,
  applicationsList,
  boxStyles,
  DashboardApplicationsList,
}: {
  isLoading: boolean;
  applicationsList: NctRequestDataArray;
  boxStyles?: SxProps<Theme>;
  DashboardApplicationsList?: boolean;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();

  const [rejectionDialog, setRejectionDialog] = useState(false);

  const [rejectedApplication, setRejectedApplication] = useState<ApplicationProps>();
  const [isDialogLoading, setDialogLoading] = useState(false);
  const [id, setId] = useState('');
  const [checkPoaOrNI, setCheckPoaOrNI] = useState(false);
  const [tooltip, setTooltip] = useState('Click to Copy');
  const [applicationRefsList, setApplicationRefsList] = useState<investmentDetailsAplicationRef[]>(
    []
  );
  const [open, setOpen] = useState(false);
  const [draftLoading, setDraftLoading] = useState('');
  const [deletePopUp, setDeletePopUp] = useState<{ id: string } | null>(null);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [applicationsListData, setApplicationsListData] = useState<NctRequestDataArray>([]);
  const [isApiLoading, setApiLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const {
    page,
    search: storeSearch,
    status: storeStatus,
    applicationType: storeApplicationType,
    modeOfHolding: storeModeOfHolding,
    distributorId: storeDistributorId,
    kycStatus: storeKycStatus,
    riskCategory: storeRiskCategory,
    schemeId: storeSchemeId,
    planId: storePlanId,
  } = useSelector((store: RootStateType) => store.paramsNct);

  const fetchApplications = async (
    page: number,
    search: string,
    filters: Record<string, string>
  ) => {
    setApiLoading(true);
    setApplicationsListData([]);
    try {
      const response = (await dispatch(
        getAllNctApplications({
          page,
          search,
          ...filters,
        })
      )) as unknown as GetAllNctResponseBody;

      const { data, pages } = response || {};
      setApplicationsListData(data);
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setApiLoading(false);
    }
  };

  const memoizedfilters = useMemo(
    () => ({
      applicationType: storeApplicationType.toString(),
      distributorId: storeDistributorId.toString(),
      status: storeStatus.toString(),
      modeOfHolding: storeModeOfHolding.toString(),
      kycStatus: storeKycStatus.toString(),
      riskCategory: storeRiskCategory.toString(),
      schemeId: storeSchemeId.toString(),
      planId: storePlanId.toString(),
    }),
    [
      storeApplicationType,
      storeDistributorId,
      storeStatus,
      storeModeOfHolding,
      storeKycStatus,
      storeRiskCategory,
      storeSchemeId,
      storePlanId,
    ]
  );

  const handleClose = () => {
    setOpen(false), setId('');
  };
  const handleDialogClose = () => {
    setDialogLoading(false);
    setOpen(true);
  };

  const handleRejectionDialogClose = () => {
    setRejectionDialog(false);
  };
  useEffect(() => {
    setApplicationsListData(applicationsList);
  }, [applicationsList]);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        if (id) {
          const responseApplicationRefsList = (await dispatch(
            ApplicationRefs(id)
          )) as unknown as investmentDetailsAplicationRef[];
          if (!componentIsAlive) {
            return;
          }
          setApplicationRefsList(responseApplicationRefsList);
          handleDialogClose();
        }
      } catch (e) {
        setDialogLoading(false);
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [id]);

  const additionalCell = {
    header: '',
    renderEditDeleteCell: (row: ApplicationNctProps): JSX.Element => {
      const [loadingPopup, setLoadingPopup] = useState(false);
      const [fetchLoading, setFetchLoading] = useState(false);

      const handleLoadingPopupClose = () => {
        setLoadingPopup(false);
      };
      return (
        <Box display="flex">
          {APPLICATION_LISTING_STATUS[row.status] === APPLICATION_LISTING_STATUS.rejected && (
            <IconButton>
              <CardMedia
                component="img"
                src="/images/comment.svg"
                alt="comment"
                sx={{ width: '20px' }}
                onClick={() => {
                  setRejectedApplication(row);
                  setRejectionDialog(true);
                }}
              />
            </IconButton>
          )}
          {!['draft', 'rejected', 'sent_to_poa_approver', 'sent_to_amc_approver2'].includes(
            row.status
          ) && (
            <Tooltip title="Link">
              <IconButton
                sx={{
                  p: 0,
                  ':hover': {
                    bgcolor: '#F4FCFC',
                  },
                }}
                onClick={() => {
                  setId(row.id as string),
                    setDialogLoading(true),
                    setCheckPoaOrNI(row.hasPOA || checkIfApplicationIsNonIndividual(row));
                }}>
                <CardMedia
                  component="img"
                  src="/images/link-icon.svg"
                  alt="Link"
                  sx={{ width: '30px' }}
                />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={row.editable ? 'Edit' : ''}>
            <IconButton
              onClick={() => {
                row.editable &&
                  history.push(
                    checkIfApplicationIsNonIndividual(row)
                      ? `/non-individual-kyc-update`
                      : `/individual-kyc-update`,
                    { id: row.id }
                  );
              }}
              sx={{
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                '&.Mui-disabled': {
                  cursor: row.editable ? 'pointer' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              disabled={!row.editable}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {[USER_ROLES.RM, USER_ROLES.DISTRIBUTOR, USER_ROLES.SUBDISTRIBUTOR].includes(role) && (
            <Tooltip title={row.deletable ? 'Delete' : 'Application can`t be Deleted'}>
              <IconButton
                sx={{
                  p: 0,
                  pr: 0.5,
                  ':hover': {
                    bgcolor: '#F4FCFC',
                  },
                  '&.Mui-disabled': {
                    cursor: row.deletable ? 'pointer' : 'not-allowed !important',
                    pointerEvents: 'all',
                  },
                }}
                onClick={() => row.deletable && setDeletePopUp({ id: row.id })}
                disabled={!row.deletable}>
                <DeleteOutlineIcon color={row.deletable ? 'error' : 'disabled'} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={
              row?.signedLeegalityFile?.url ? 'Download' : 'Only Signed Documents can be Downloaded'
            }>
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                cursor: 'pointer',
                pointerEvents: 'all',
              }}
              onClick={async () => {
                try {
                  row?.signedLeegalityFile?.url &&
                    (await handleDownload(
                      row?.signedLeegalityFile?.url as string,
                      `${row.applicationNumber}.pdf`
                    ));
                } catch (e) {
                  console.error((e as Error).message);
                }
              }}>
              <CardMedia
                component="img"
                src={
                  row?.signedLeegalityFile?.url
                    ? '/images/download-icon.png'
                    : '/images/download-icon-disabled.png'
                }
                alt="Download"
                //sx={{ width: '30px' }}
              />
            </IconButton>
          </Tooltip>
          {[
            APPLICATION_LISTING_STATUS.sent_to_applicant1,
            APPLICATION_LISTING_STATUS.sent_to_authorised_signatories,
          ].includes(APPLICATION_LISTING_STATUS[row.status]) &&
            [USER_ROLES.AMC_ADMIN, USER_ROLES.RM].includes(role) && (
              <LoadingButton
                loadingPosition="start"
                variant="contained"
                sx={{
                  color: 'common.white',
                  lineHeight: '14px',
                  fontSize: 10,
                  width: 80,
                  ml: 1,
                }}
                loading={draftLoading === row?.applicationNumber}
                onClick={async () => {
                  setDraftLoading(row.applicationNumber);
                  try {
                    await dispatch(
                      changeStatusToDraft({
                        applicationNumber: row.applicationNumber,
                        deleteLeegalityDocument: true,
                      })
                    );
                    setDraftLoading('');
                    if (DashboardApplicationsList) {
                      setDialogLoading(true);
                      const response = (await dispatch(
                        getAllApplications({
                          page: 1,
                          limit: 4,
                        })
                      )) as unknown as GetAllNctResponseBody;
                      const { data } = response || {};
                      setApplicationsListData(data);
                      setDialogLoading(false);
                    } else {
                      setDialogLoading(true);
                      const applicationResponse = (await dispatch(
                        getAllNctApplications({
                          page,
                          search: storeSearch,
                          status: storeStatus,
                          applicationType: storeApplicationType,
                          modeOfHolding: storeModeOfHolding,
                          distributorId: storeDistributorId,
                          kycStatus: storeKycStatus,
                          riskCategory: storeRiskCategory,
                          schemeId: storeSchemeId,
                          planId: storePlanId,
                        })
                      )) as unknown as GetAllNctResponseBody;
                      const { data } = applicationResponse || {};
                      setApplicationsListData(data);
                      setDialogLoading(false);
                    }
                  } catch (e) {
                    setDraftLoading('');
                    setDialogLoading(false);
                    console.error((e as Error).message);
                  }
                }}>
                Move to Draft
              </LoadingButton>
            )}
          <Tooltip title={'Fetch Kyc'}>
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                cursor: 'pointer',
                pointerEvents: 'all',
              }}
              onClick={async () => {
                setFetchLoading(true);
                try {
                  await dispatch(
                    fetchMismatchInvestors({
                      applicationIds: [row?.id],
                      toastMessage: '',
                    })
                  );
                  await fetchApplications(page, storeSearch, memoizedfilters);
                } catch (e) {
                  console.error((e as Error).message);
                } finally {
                  setFetchLoading(false);
                }
              }}>
              <AutorenewIcon
                sx={{
                  width: 35,
                  height: 22,
                  animation: fetchLoading ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
          <LoadingDialog
            loadingPopup={loadingPopup}
            onLoadingPopupClose={handleLoadingPopupClose}
            loaderText={'Downloading'}
          />
        </Box>
      );
    },
  };

  const additionalCellForInvestors = {
    header: '',
    renderEditDeleteCell: (row: NctRequestData): JSX.Element => {
      const [loadingPopup, setLoadingPopup] = useState(false);
      const [fetchLoading, setFetchLoading] = useState(false);

      const handleLoadingPopupClose = () => {
        setLoadingPopup(false);
      };
      return (
        <Box display="flex">
          <Tooltip title={'Fetch Kyc'}>
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                cursor: 'pointer',
                pointerEvents: 'all',
              }}
              onClick={async () => {
                setFetchLoading(true);
                try {
                  await dispatch(
                    fetchMismatchInvestors({
                      applicationIds: row?.applicationIds,
                      toastMessage: '',
                      params: {
                        fetch: 'all',
                      },
                    })
                  );
                  await fetchApplications(page, storeSearch, memoizedfilters);
                } catch (e) {
                  console.error((e as Error).message);
                } finally {
                  setFetchLoading(false);
                }
              }}>
              <AutorenewIcon
                sx={{
                  width: 35,
                  height: 22,
                  animation: fetchLoading ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
          <LoadingDialog
            loadingPopup={loadingPopup}
            onLoadingPopupClose={handleLoadingPopupClose}
            loaderText={'Downloading'}
          />
        </Box>
      );
    },
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      <DataTable
        isLoading={isLoading || isApiLoading}
        tableHeader={[...getInvestorTableHeader(isMobile), additionalCellForInvestors]}
        tableData={applicationsListData}
        rowCustomStyles={{
          '&:last-child td, &:last-child th': { border: 0 },
          '.MuiTableCell-body ': { color: '#686e72' },
          background: 'transparent',
        }}
        boxStyles={boxStyles}
        rowExpandable={true}
        childTableHeader={[...getApplicationTableHeader(role), additionalCell]}
      />

      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={4}
          pb={2}>
          <Typography sx={{ color: 'primary.main', fontSize: '20px', fontWeight: 500 }}>
            Get Link
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Grid>
        {applicationRefsList &&
          applicationRefsList.map((ref) => {
            return (
              <React.Fragment key={ref.id}>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  px={4}
                  py={1}
                  sx={{
                    ':hover': {
                      bgcolor: '#F4FCFC',
                    },
                    ':last-of-type': {
                      mb: 5,
                    },
                  }}>
                  <Typography
                    sx={{
                      color: 'rgba(41, 49, 57, 0.7)',
                      fontSize: '18px',
                      fontWeight: 500,
                    }}>
                    Copy Link For {ref.name}
                  </Typography>
                  <Tooltip title={tooltip}>
                    <IconButton
                      onMouseOver={() => {
                        setTooltip('Copy Link');
                      }}
                      onClick={() => {
                        setTooltip('Copied'),
                          navigator.clipboard.writeText(
                            checkPoaOrNI
                              ? ref.invitationUrl
                              : ref?.signatureBy &&
                                ('fundManager' === ref.signatureBy ||
                                  USER_ROLES.TRUSTEE === ref.signatureBy)
                              ? ref.invitationUrl
                              : `${window.location.origin}/investment-details/${ref.referenceId}/application-details`
                          );
                      }}>
                      <CardMedia
                        component="img"
                        src="/images/link-icon.svg"
                        alt="Link"
                        sx={{ width: '35px' }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </React.Fragment>
            );
          })}
      </Dialog>
      <Dialog
        onClose={handleDialogClose}
        open={isDialogLoading}
        sx={{
          '.MuiPaper-root ': {
            p: 4,
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
            },
          }}>
          <Typography sx={{ color: 'primary.main', lineHeight: '35px', textAlign: 'center' }}>
            Loading...
          </Typography>
        </Box>
      </Dialog>
      <Dialog
        onClose={handleRejectionDialogClose}
        open={rejectionDialog}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <IconButton
          onClick={handleRejectionDialogClose}
          sx={{ alignSelf: 'flex-end', mt: 2, mr: 2 }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        {rejectedApplication && (
          <Box
            sx={{
              width: { xs: '80%', sm: '80%' },
              mx: 'auto',
              '.MuiTypography-root': {
                fontWeight: 500,
              },
              mt: 1,
              mb: 5,
            }}>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 6.7 },
                mt: 1,
              }}>
              Application Id:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.applicationNumber}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 1.5 },
                mt: 1,
              }}>
              Application Status:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#BD2932',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {POA_APPLICATION_STATUS[rejectedApplication.status]}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9 },
                mt: 1,
              }}>
              Rejected on:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {formatShortDateAndTime(rejectedApplication.rejectedAt)}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9.5 },
                mt: 1,
              }}>
              Rejected by:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {REJECTED_ROLES[rejectedApplication.rejectedByRole]}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                mt: 1,
              }}>
              Reason for rejection:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.rejectedReason}
              </Typography>
            </Typography>
            <Button
              variant="contained"
              sx={{ ml: '32%', lineHeight: 1.1, width: '30%', mt: 3 }}
              onClick={handleRejectionDialogClose}>
              Done
            </Button>
          </Box>
        )}
      </Dialog>
      <ConfirmationDialog
        message={'Are you sure you want to delete Application ?'}
        open={deletePopUp !== null}
        setOpen={() => setDeletePopUp(null)}
        onSave={async () => {
          try {
            setDeletePopUp(null);
            const _application = (await dispatch(
              getApplicationDetails(deletePopUp?.id || '', 'delete')
            )) as unknown as ApplicationProps;
            if (DashboardApplicationsList) {
              setDialogLoading(true);
              const response = (await dispatch(
                getAllApplications({
                  page: 1,
                  limit: 4,
                })
              )) as unknown as GetAllNctResponseBody;
              const { data } = response || {};
              setApplicationsListData(data);
              setDialogLoading(false);
            } else {
              setApplicationsListData(
                applicationsListData.filter(
                  (application) => application.id !== deletePopUp?.id || ''
                )
              );
            }
            enqueueSnackbar('Application Deleted Successfully', {
              variant: 'success',
              autoHideDuration: 3000,
            });
          } catch (e) {
            setDialogLoading(false);
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDeletePopUp(null)}
      />
    </>
  );
}
